import 'src/styles/printer-page-tabs.css';

import { faInfoCircle, faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ActionContext from 'src/components/action-context';
import { BatchCardPreviewContainer } from 'src/components/BatchCardPreviewContainer';
import ContainerQRCodes from 'src/components/ContainerQRCodes';
import Header from 'src/components/header.jsx';
import Loader from 'src/components/loader';
import NotFound from 'src/components/not-found';
import RunQueue from 'src/components/RunQueue';
import Alert from 'src/components/ui/Alert';
import Tabs from 'src/components/ui/Tabs';
import WorkstationCard from 'src/components/WorkstationCard';
import {
  arePrinterModulesEnabled,
  PRINTER_PAGE_TAB,
  PRINTER_PAGE_TABS,
} from 'src/constants/printer-page-tabs';
import FeaturesContext from 'src/context/FeaturesContext';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { api } from 'src/utils/api';
import {
  API_RESOURCES,
  MATERIAL_BATCH_ACTIONS,
  PERMANENT_CONTAINER_ACTIONS,
} from 'src/utils/constants';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import { getRouteURI, getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

import routes from '../../../utils/routes';

const PrinterMaterialPage = ({ user }) => {
  const { uuid: printerUuid } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [pageState, setPageState] = useState({
    id: printerUuid,
    printer: null,
    materialBatches: null,
    error: null,
    batchSubLocation: null,
  });

  const {
    containerActionData,
  } = useContext(ActionContext);

  const { features } = useContext(FeaturesContext);
  const isMaintenanceEnabled = isFeatureEnabled(features, FEATURES.EQUIPMENT_MAINTENANCE);
  const areWorkstationModulesLocked = !arePrinterModulesEnabled(features);

  const { openActionPanel } = useActionPanelStore();

  const resetUnloadMaterialState = () => {
    // Reset the context state to its initial state
    containerActionData.resetActionContainerState();
  };

  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const printer = await api.get(`${API_RESOURCES.PRINTER}/${printerUuid}/`).json();
      const { resources: materialBatchesAtMachine } = await api.get(`${API_RESOURCES.MATERIAL_BATCH}/`, {
        searchParams: {
          'filter[at_machine]': printer.uri,
        },
      }).json();

      const batchData = materialBatchesAtMachine?.[0];

      const batchSubLocation = batchData ?
        await api.get(`${API_RESOURCES.SUB_LOCATION}/${getUuid(materialBatchesAtMachine[0].sub_location)}/`).json() :
        null;

      setPageState((prevState) => ({
        ...prevState,
        id: printerUuid,
        printer,
        materialBatches: materialBatchesAtMachine,
        batchSubLocation: batchSubLocation?.name,
      }));
    } catch (error) {
      if (error.name === 'HTTPError' && error.response.status === 404) {
        setPageState((prevState) => ({
          ...prevState,
          error: {
            name: error.name,
            status: error.response && error.response.status,
          },
        }));
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, [printerUuid]);

  const handleShowQRCodes = () => {
    openActionPanel({
      title: 'Print QR Codes',
      content: <ContainerQRCodes
        printer={pageState.printer}
        containers={pageState.containers}
        backUri={getRouteURI(routes.printerMaterial, { uuid: printerUuid })}
        forResource={pageState.printer?.uri}
      />,
    });
  };


  if (pageState.error) {
    return (
      <>
        <Header excludeFromHistory title="Not Found" user={user} />
        <main role="main" className="text-center">
          <NotFound id={pageState.id} />
        </main>
      </>
    );
  }
  if (isLoading) {
    return <Loader />;
  }

  const materialBatch = pageState.materialBatches[0];
  const isBatchSummaryVisible = Boolean(materialBatch);

  const renderWorkstationAction = ({ to, disabled, onClick, children }) => {
    const button = (
      <button
        type="button"
        disabled={disabled}
        className="btn btn-lg btn-primary btn-block fs-mobile-18"
        onClick={disabled ? undefined : onClick}
      >
        {children}
      </button>
    );

    if (disabled || !to) {
      return <div className="link-btn">{button}</div>;
    }

    return (
      <Link to={to} className="link-btn">
        {button}
      </Link>
    );
  };

  return (
    <>
      <Header
        title="Printer Information"
        user={user}
        historyLabel="Printer"
        historyDetail={pageState.printer?.name && `(${pageState.printer.name})`}
        onPrintQRCodesClick={handleShowQRCodes}
      />
      <main role="main" className="printer-page">
        <WorkstationCard
          workstation={pageState.printer}
          maintenanceRoute={isMaintenanceEnabled ? routes.printerMaintenance : null}
        />
        <Tabs tabs={PRINTER_PAGE_TABS}>
          {tab => (tab === PRINTER_PAGE_TAB.QUEUE ? (
            <RunQueue workstation={pageState.printer} filterKey="printer" />
        ) : (
          <>
            {areWorkstationModulesLocked && (
              <Alert variant="info" icon={faInfoCircle} title="Information">
                The features below require additional modules. Please speak to your Authentise
                Representative if you would like them enabled.
              </Alert>
            )}
            {
              isBatchSummaryVisible &&
              <BatchCardPreviewContainer isExpanded batchUuid={materialBatch.uuid} />
            }
            {renderWorkstationAction({
              to: getRouteURI(
                routes.scan,
                {},
                {
                  printer: pageState.printer.uri,
                  entity: API_RESOURCES.MATERIAL_BATCH,
                  action: materialBatch
                    ? MATERIAL_BATCH_ACTIONS.MACHINE_TOP_OFF
                    : MATERIAL_BATCH_ACTIONS.MACHINE_LOAD,
                  actionFromPrinter: true,
                }
              ),
              disabled: areWorkstationModulesLocked,
              children: materialBatch ? 'Load Material (Top Off)' : 'Load Material',
            })}
            {renderWorkstationAction({
              to: getRouteURI(
                routes.scan,
                {},
                {
                  printer: pageState.printer.uri,
                  'material-batch': materialBatch?.uri,
                  entity: API_RESOURCES.MATERIAL_CONTAINER,
                  action: PERMANENT_CONTAINER_ACTIONS.UNLOAD_UNUSED_MATERIAL,
                  actionFromPrinter: true,
                }
              ),
              disabled: areWorkstationModulesLocked || !materialBatch,
              onClick: resetUnloadMaterialState,
              children: 'Unload Unused Material',
            })}
            {renderWorkstationAction({
              to: getRouteURI(
                routes.scan,
                {},
                {
                  printer: pageState.printer.uri,
                  'material-batch': materialBatch?.uri,
                  entity: API_RESOURCES.MATERIAL_CONTAINER,
                  action: PERMANENT_CONTAINER_ACTIONS.UNLOAD_RECLAIMED_MATERIAL,
                  actionFromPrinter: true,
                }
              ),
              disabled: areWorkstationModulesLocked || !materialBatch,
              onClick: resetUnloadMaterialState,
              children: 'Complete Print Job: Unload Reclaimed Powder',
            })}
            {renderWorkstationAction({
              to: getRouteURI(
                routes.materialBatchAction,
                { uuid: getUuid(materialBatch?.uri) },
                {
                  type: MATERIAL_BATCH_ACTIONS.MACHINE_UNLOAD_CHAMBER,
                  actionFromPrinter: true,
                  printer: pageState.printer.uri,
                }
              ),
              disabled: areWorkstationModulesLocked || !materialBatch,
              children: 'Complete Print Job: Record Build Weight',
            })}
            {isMaintenanceEnabled &&
              renderWorkstationAction({
                to: getRouteURI(routes.printerMaintenance, { uuid: printerUuid }),
                disabled: areWorkstationModulesLocked,
                children: (
                  <>
                    <FontAwesomeIcon icon={faScrewdriverWrench} className="mr-2" />
                    Maintenance
                  </>
                ),
              })}
          </>
          ))}
        </Tabs>
      </main>
    </>
  );
};

PrinterMaterialPage.propTypes = {
  user: userPropType,
};

PrinterMaterialPage.defaultProps = {
  user: null,
};

export default PrinterMaterialPage;
